import { format, isValid, parse, parseISO } from 'date-fns'

export const formatDollarNoPennies = val => {
  return formatDollar(val, true, 0, 0)
}

export const formatDollarWithPennies = val => {
  return formatDollar(val, true, 0, 2)
}

// Used by the schedules, dynamically, provided method name via API
// See BaseSchedule.getTextFunction
export const formatLimit = (valStr, withSpace) => {
  // Check if the string is numeric
  if (valStr == null) {
    return valStr
  }
  if (/^\d{1,3}(,?\d{3})*(\.\d+)?$/.test(valStr)) {
    try {
      const numericString = isNumber(valStr) ? valStr : valStr.replace(/,/g, '')
      return formatDollarOptionalPennies(numericString, withSpace)
    } catch (e) {
      console.log('error formatLimit: ' + valStr, e)
      return valStr
    }
  } else {
    return valStr
  }
}

export function viewFileToText(val) {
  if (val === null || val === undefined) {
    return 'Upload File'
  }
  if (typeof val === 'boolean') {
    return val ? 'View File' : 'Upload File'
  }
  return 'Upload File'
}


export const formatLimitWithCurrency = (valStr, currencyCode) => {
  // Check if the string is numeric
  if (valStr == null) {
    return valStr
  }
  if (currencyCode === undefined) {
    currencyCode = 'USD' // Default for now
  }
  if (/^\d{1,3}(,?\d{3})*(\.\d+)?$/.test(valStr)) {
    try {
      const numericString = isNumber(valStr) ? valStr : valStr.replace(/,/g, '')
      const result = zeroOrTwoDecimals(numericString, currencyCode)
      return result
    } catch (e) {
      console.log('error formatLimit: ' + valStr, e)
      return valStr
    }
  } else {
    return valStr
  }
}
const convertTimestampToDate = timestampString => {
  if (!timestampString) {
    return timestampString
  }
  if (timestampString.length == 10) {
    timestampString = timestampString + 'T00:00:00'
  }
  const date = new Date(timestampString)
  return date
}

export const formatTimestampToCommon = timestampString => {
  if (!timestampString) {
    return timestampString
  }
  const date = convertTimestampToDate(timestampString)
  return format(date, 'MM/dd/yyyy')
}

export const formatTimestampToDate = (timestampString, useShortYear) => {
  if (!timestampString) {
    return timestampString
  }
  const date = convertTimestampToDate(timestampString)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  let day = date.getDate()

  let monthIndex = date.getMonth()
  let monthName = monthNames[monthIndex]

  let year = date.getFullYear()
  if (useShortYear) {
    year = year % 100 // grab the last 2 digits
  }

  return `${monthName} ${day}, ${year}`
}

const isNumber = val => {
  return val instanceof Number || val.constructor?.name === 'Number'
}

export const limitToVal = text => {
  let textValue = text
  if (textValue) {
    if (textValue.startsWith('$') || textValue.startsWith('€')) {
      textValue = text.slice(1).replace(/,/g, '')
    }
  }
  const numberValue = parseFloat(textValue)
  if (!isNaN(numberValue)) {
    const val = dollarToValWithPennies(textValue)
    return val
  }

  return text
}

export const formatDollar = (valStr, withSpace, minDigit, maxDigit) => {
  // $0 and blank are very different things with most monetary fields
  // ie: Deductible: $0 - I don't have to pay for insurance during a claim!!

  if (valStr == null || valStr === '') {
    return undefined
  }
  if (withSpace === undefined || withSpace === null) {
    withSpace = true
  }
  if (minDigit === undefined || minDigit === null) {
    minDigit = 2
  }
  if (maxDigit === undefined || maxDigit === null) {
    maxDigit = 2
  }
  const dollarUSLocale = Intl.NumberFormat('en-US', {
    minimumFractionDigits: minDigit,
    maximumFractionDigits: maxDigit,
  })
  const numberWithCommas = dollarUSLocale.format(valStr)
  if (withSpace) {
    return `$ ${numberWithCommas}`
  } else {
    return `$${numberWithCommas}`
  }
}

// If a whole dollar, it will not show pennies.
// If any pennies, show 2 digits
export const formatDollarOptionalPennies = (valStr, withSpace) => {
  // $0 and blank are very different things with most monetary fields
  // ie: Deductible: $0 - I don't have to pay for insurance during a claim!!

  if (valStr == null || valStr === '') {
    return undefined
  }
  if (withSpace === undefined || withSpace === null) {
    withSpace = true
  }
  const numberWithCommas = zeroOrTwoDecimals(valStr)
  if (withSpace) {
    return `$ ${numberWithCommas}`
  } else {
    return `$${numberWithCommas}`
  }
}

export const zeroOrTwoDecimals = ((input, currencyCode) => {
  const locale = 'en-US'
  // The locale: de-DE - flips commas and periods, probably too big of a change right away. Would need more testing
  const numberFormatter = new Intl.NumberFormat(locale, {
    style: currencyCode === undefined ? undefined : 'currency',
    currency: currencyCode,
    minimumFractionDigits: input % 1 === 0 ? 0 : 2, // if whole number, chop off pennies
    maximumFractionDigits: input % 1 === 0 ? 0 : 2, // if whole number, chop off pennies
  });
  return numberFormatter.format(input)
});

export const numberToTextKeepWords = text => {
  let textValue = text
  const numberValue = parseFloat(textValue)
  if (!isNaN(numberValue)) {
    const val = numberToText(text)
    return val
  }
  return text
}

// :to-text="dollarToText" :to-value="dollarToVal"
// 12345 -> 12,345
// 12345.12 -> 12,345.12
export const numberToText = val => {
  if (val === undefined || val === null) {
    return val
  }
  const dollarUSLocale = Intl.NumberFormat('en-US')
  return dollarUSLocale.format(val)
}
// By Default: 12345.12 -> 12345;
// With allowDecimals true: 1.1 -.
export const numberToVal = (text, allowDecimals) => {
  if (text === undefined || text === null) {
    return text
  }

  let output = text.trim()
  if (!allowDecimals) {
    const indexOfPeriod = output.indexOf('.')
    if (indexOfPeriod > 0) output = output.substring(0, indexOfPeriod)
  }

  output = output.replace(/[$,]/g, '')
  return output
}

export const dollarToVal = text => {
  if (text == null || text === '') {
    return undefined
  }
  let output = text.trim()
  const indexOfPeriod = output.indexOf('.')
  if (indexOfPeriod > 0) output = output.substring(0, indexOfPeriod)
  output = output.replace(/[$,]/g, '')
  return parseFloat(output)
}

export const dollarToValWithPennies = text => {
  if (text == null || text === '') {
    return undefined
  }
  let output = text.trim()
  output = output.replace(/[$,€]/g, '')
  const float = parseFloat(output)
  return Math.round(float * 100) / 100 // rounds to the nearest penny
}

export const dollarWithPenniesToVal = text => {
  if (text == null || text === '') {
    return undefined
  }
  let output = text.trim()
  output = output.replace(/[$,]/g, '') // Remove dollar signs and commas
  const value = parseFloat(output)
  if (isNaN(value)) {
    return undefined
  }
  return Number(value.toFixed(2)) // Keep two decimal places
}

export function uuidv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
  )
}

export function formatIfNumber(val) {
  if (val) {
    val = val.trim()
  }
  const regex = /^[\d.,-]+$/
  if (regex.test(val)) {
    const cleanString = val.replace(/[^\d.-]/g, '')
    return numberToText(cleanString)
  }
  return val
}
export function booleanToYnText(val) {
  if (val === null || val === undefined) {
    return undefined
  }
  if (typeof val === 'boolean') {
    return val ? 'Y' : 'N'
  }
  return val
}
export function ynTextToVal(text) {
  if (text === undefined || text === null || text === '') {
    return undefined
  }
  let output = text.trim()
  if (output && 'Y' === output.toUpperCase()) {
    return true
  }
  return false
}

// 2024-06-20 => 06/20/2024
export function isoDateToCommonDate(isoString) {
  if (!isoString) return null

  const isoDateFormat = /^\d{4}-\d{2}-\d{2}$/
  if (isoDateFormat.test(isoString)) {
    const commonDate = parse(isoString, 'MM/dd/yyyy', new Date())
    if (isValid(commonDate) && isoString === format(commonDate, 'MM/dd/yyyy')) {
      return isoString
    }
    return format(parseISO(isoString), 'MM/dd/yyyy')
  } else {
    console.log('Did not match format - yyyy-mm-dd')
    return isoString
  }
}

// Attempts to convert the most common date formats to ISO Date
export function userInputDateToIsoDate(userInput) {
  if (!userInput) return null

  const commonDateFormat = /^\d{1,2}\/\d{1,2}\/\d{4}$/
  if (commonDateFormat.test(userInput)) {
    const parsed = parse(userInput, 'MM/dd/yyyy', new Date())
    return format(parsed, 'yyyy-MM-dd')
  }

  const isoDateFormat = /^\d{4}-\d{2}-\d{2}$/
  if (isoDateFormat.test(userInput)) {
    return userInput
  }

  const commonDateDashFormat = /^\d{1,2}-\d{1,2}-\d{4}$/
  if (commonDateDashFormat.test(userInput)) {
    const parsed = parse(userInput, 'MM-dd-yyyy', new Date())
    return format(parsed, 'yyyy-MM-dd')
  }
  const commonDateNoSymFormat = /^\d{2}\d{2}\d{4}$/
  if (commonDateNoSymFormat.test(userInput)) {
    const parsed = parse(userInput, 'MMddyyyy', new Date())
    return format(parsed, 'yyyy-MM-dd')
  } else {
    console.log('Unsupported Date Format')
    return userInput
  }
}

export function percentToText(val) {
  if (val === 0 || val === '0') {
    return '0%'
  }
  if (!val) {
    return undefined
  }
  return `${val}%`
}
export function percentToVal(text) {
  if (text === undefined || text === null) {
    return undefined
  }
  let output = text.trim()
  return parseFloat(output)
}

export function createTitle(s) {
    if (s != null) {
      return s
          .replaceAll('_', ' ')
          .toLowerCase()
          .split(' ')
          .map(s => s.charAt(0).toUpperCase() + s.substring(1))
          .join(' ')
    }
    return s
}